import { Link } from "react-router-dom";
import { ShoppingBag, ArrowRight } from "lucide-react";
import { useSite } from "../context/SiteContext";
import { api } from "../lib/api";

const AndroidIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M17.6 9.48l1.84-3.18a.38.38 0 0 0-.66-.38l-1.86 3.22a11.6 11.6 0 0 0-9.84 0L5.22 5.92a.38.38 0 0 0-.66.38L6.4 9.48A10.9 10.9 0 0 0 1 18h22a10.9 10.9 0 0 0-5.4-8.52zM7 15.25a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5zm10 0a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5z" />
    </svg>
);

const AppleIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M16.36 12.76c0-2.26 1.85-3.34 1.93-3.39-1.05-1.54-2.69-1.75-3.27-1.77-1.39-.14-2.71.82-3.41.82-.7 0-1.79-.8-2.94-.78-1.51.02-2.9.88-3.68 2.23-1.57 2.72-.4 6.75 1.13 8.96.75 1.08 1.64 2.3 2.81 2.25 1.13-.04 1.55-.73 2.92-.73s1.74.73 2.94.7c1.21-.02 1.98-1.1 2.72-2.19.86-1.25 1.21-2.46 1.23-2.52-.03-.01-2.36-.9-2.38-3.58zM14.02 6.1c.62-.75 1.04-1.8 1.04-2.84 0-.14-.01-.29-.04-.4-1.06.04-2.33.71-3.08 1.6-.6.7-1.13 1.81-1.11 2.88 1.18.09 2.57-.6 3.19-1.24z" />
    </svg>
);

const hasConsent = () => localStorage.getItem("bc_consent") === "yes";
const visitorId = () => localStorage.getItem("bc_vid") || "";

/** Événement canonique — ne bloque jamais la navigation. */
export function fireEvent(type, target = "") {
    api
        .post("/public/events", { type, target, visitor_id: visitorId(), consent: hasConsent() })
        .catch(() => {});
}

const CTA_BASE =
    "group relative overflow-hidden rounded-2xl font-heading font-semibold transition-all duration-300 select-none " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 active:scale-[0.97]";

const Shine = () => (
    <span
        className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/15 to-transparent motion-reduce:hidden"
        aria-hidden="true"
    />
);

function StoreCTA({ platform, link, dark }) {
    const Icon = platform === "android" ? AndroidIcon : AppleIcon;
    const enabled = link?.enabled && link?.url;

    const go = () => {
        // La mesure ne bloque jamais l'ouverture du lien.
        window.open(link.url, "_blank", "noopener,noreferrer");
        api.post("/public/track/click", { platform, consent: hasConsent() }).catch(() => {});
    };

    if (enabled) {
        const style =
            platform === "android"
                ? "bg-gradient-to-br from-forest to-forest-surface text-ivory shadow-lg shadow-forest/25 hover:shadow-xl hover:shadow-forest/30 hover:-translate-y-0.5"
                : "bg-gradient-to-br from-ink to-forest-deep text-ivory shadow-lg shadow-ink/25 hover:shadow-xl hover:-translate-y-0.5";
        return (
            <div className="flex flex-col gap-2 w-full sm:w-auto">
                <button
                    onClick={go}
                    className={`${CTA_BASE} ${style} w-full sm:w-auto px-8 py-4`}
                    data-testid={`download-${platform}-button`}
                    aria-label={`${link.label || "Télécharger"} — ouvre le magasin d'applications dans un nouvel onglet`}
                >
                    <Shine />
                    <span className="relative flex items-center justify-center gap-3">
                        <Icon />
                        <span>{link.label || (platform === "android" ? "Télécharger sur Google Play" : "Télécharger sur l'App Store")}</span>
                    </span>
                </button>
                {link.badge && (
                    <span className={`text-xs font-mono text-center tracking-wide ${dark ? "text-gold-light" : "text-gold-dark"}`} data-testid={`${platform}-badge`}>
                        {link.badge}
                    </span>
                )}
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2 w-full sm:w-auto">
            <div
                role="button"
                aria-disabled="true"
                aria-label={link?.unavailable_message || "Bientôt disponible"}
                className={`${CTA_BASE} w-full sm:w-auto px-8 py-4 cursor-not-allowed border ${
                    dark ? "border-ivory/25 bg-ivory/5 text-ivory/60" : "border-dashed border-gold/50 bg-white/70 text-ink-soft"
                }`}
                data-testid={`download-${platform}-disabled`}
            >
                <span className="relative flex items-center justify-center gap-3">
                    <Icon />
                    <span>{platform === "android" ? "Google Play" : "App Store"}</span>
                </span>
            </div>
            <span className={`text-xs text-center ${dark ? "text-ivory/60" : "text-ink-soft"}`} data-testid={`${platform}-unavailable-message`}>
                {link?.unavailable_message || "Bientôt disponible"}
            </span>
        </div>
    );
}

export function StoreButtons({ dark = false, size = "default" }) {
    const { data } = useSite();
    const links = data?.store_links || {};
    return (
        <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto" data-testid="store-buttons">
            <StoreCTA platform="android" link={links.android} dark={dark} />
            <StoreCTA platform="ios" link={links.ios} dark={dark} />
        </div>
    );
}

export function BoutiqueCTA({ label = "Découvrir la boutique", className = "" }) {
    return (
        <Link
            to="/boutique"
            onClick={() => fireEvent("boutique_click", "cta")}
            className={`${CTA_BASE} inline-flex items-center justify-center gap-3 px-8 py-4 border-2 border-gold text-forest hover:bg-gold hover:text-white hover:shadow-lg hover:shadow-gold/25 hover:-translate-y-0.5 w-full sm:w-auto ${className}`}
            data-testid="boutique-cta"
            aria-label="Découvrir la boutique BODYCOACHIA"
        >
            <ShoppingBag size={19} />
            <span>{label}</span>
            <ArrowRight size={18} className="group-hover:translate-x-1.5 transition-transform duration-300 motion-reduce:transition-none" />
        </Link>
    );
}
